import React, {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useId,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  List,
  Typography,
  Grid,
  Container,
  Input,
  TextField,
  LinearProgress,
  IconButton,
} from "@mui/material";
import theme, { colors } from "../../theme";
import GreenTicksListItem from "../../atoms/GreenTicksListItem";
import { IDesign } from "../../types";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import useShopifyApi from "../../hooks/useShopifyApi";
import useWebsiteApi from "../../hooks/useWebsiteApi";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "@mui/icons-material/Delete";
import Bugsnag from "@bugsnag/js";
import { AxiosProgressEvent } from "axios";

export type StartDesignProps = {
  cartPath: string;
  design: IDesign;
  graphicEnabled?: boolean;

  translations: {
    youCan: string;
    uploadArtworkTitle: string;
    uploadArtworkDescription: string;
    uploadArtworkButtonText: string;
    uploadArtworkFeatures: string[];
  };
};

type FileState = {
  id: number | string;
  f: File;
  uploadState: "uploading" | "uploaded" | "error";
  uploadProgress: number;
};

const StartDesign: FunctionComponent<StartDesignProps> = (
  props: StartDesignProps
) => {
  const inputRef = useRef<any>(null);
  const [isAddingToCart, setAddingToCart] = useState(false);
  const websiteApi = useWebsiteApi();
  const shopifyApi = useShopifyApi();
  const uploadButtonId = useId();
  const [files, setFiles] = useState<FileState[]>([]);
  const isUploading = files.some((fs) => fs.uploadState == "uploading");
  const [instructions, setInstructions] = useState("");

  const handleAddToCart = async (
    filesUploaded: {
      src: string;
      id: string;
      contentType: string;
    }[]
  ) => {
    const key = enqueueSnackbar("Adding to cart", {
      variant: "info",
      autoHideDuration: 30000,
    });
    setAddingToCart(true);

    try {
      await shopifyApi.addToCart(
        { ...props.design, instructions },
        filesUploaded
      );
      window.location.pathname = props.cartPath;
    } catch (err) {
      enqueueSnackbar("Error adding to the cart", {
        variant: "error",
      });
      Bugsnag.notify(err);
      console.error(err);
    } finally {
      setAddingToCart(false);
      closeSnackbar(key);
    }
  };

  const progressEventFactory = (fs: FileState) => {
    return (progressEvent: AxiosProgressEvent) => {
      setFiles((files) => {
        return files.map((fs2) => {
          if (fs.id === fs2.id) {
            fs2.uploadProgress = progressEvent.progress || 0;
            fs2.uploadState = "uploading";
          }

          return fs2;
        });
      });
    };
  };

  const handleFileUploaded = (id: FileState["id"], newId: string) => {
    setFiles((files) => {
      return files.map((fs2) => {
        if (fs2.id === id) {
          fs2.id = newId;
          fs2.uploadState = "uploaded";
          fs2.uploadProgress = 100;
        }

        return fs2;
      });
    });
  };

  const handleOnFileUpload = async (ev) => {
    const fileEv = ev as ChangeEvent<HTMLInputElement>;

    if (fileEv.target.files) {
      enqueueSnackbar(`Uploading ${fileEv.target.files.length} files`, {
        variant: "info",
      });

      const filesToUpload = fileEv?.target?.files || [];
      const filesArr = Array.from(filesToUpload)
        .flat()
        .filter((f) => f)
        .map((f) => {
          const fs: FileState = {
            id: new Date().getTime() + Math.random() * 10000,
            f,
            uploadProgress: 0,
            uploadState: "uploading",
          };

          return fs;
        });

      setFiles((oldFiles) => oldFiles.concat(filesArr));

      const allUploaded = Promise.all(
        filesArr.map((fs, idx) => {
          const progress = progressEventFactory(fs);
          return websiteApi.uploadArtwork(fs.f, progress).then((upload) => {
            enqueueSnackbar(`Uploaded file ${idx + 1}/${filesArr.length}`, {
              variant: "success",
            });

            handleFileUploaded(fs.id, upload.id);

            return upload;
          });
        })
      );

      const filesUploaded = await allUploaded;
      if (filesUploaded.length > 1) {
        enqueueSnackbar("Uploaded all files", {
          variant: "success",
        });
      }

      // handleAddToCart(filesUploaded);
    }
  };

  const handleRemoveFile = (id: FileState["id"]) => {
    setFiles((files) => {
      return files.filter((fs2) => id !== fs2.id);
    });
  };

  const handleFormSubmit = (ev: FormEvent<HTMLDivElement>) => {
    ev.preventDefault();

    handleAddToCart(
      files
        .filter((fs) => typeof fs.id === "string")
        .map((fs) => {
          return {
            id: fs.id.toString(),
            contentType: fs.f.type,
            src: "",
          };
        })
    );
  };

  const maxFiles = props.design.productSku.includes("SAMPLE") ? 1 : 5;
  const multipleFileUploadsAllowed = maxFiles > 1;
  const maxFilesReached = files.length >= maxFiles;

  return (
    <>
      <Container
        component="form"
        sx={{
          border: {
            xs: "none",
            md: `1px solid ${theme.colors.greyPlaceholder}`,
          },
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          marginTop: { xs: 0, md: 5 },
          padding: { xs: 0, md: 2 },
        }}
        onSubmit={handleFormSubmit}
        maxWidth="sm"
      >
        <Box sx={{ width: "100%", padding: 2 }}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="h2">
              {props.translations.uploadArtworkTitle}
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              paddingTop={3}
              paddingBottom={5}
            >
              <label htmlFor={uploadButtonId}>
                <Input
                  ref={inputRef}
                  sx={{ display: "none" }}
                  id={uploadButtonId}
                  // disabled in MUIv5?

                  type="file"
                  inputProps={{
                    // accept: ACCEPTABLE_FILE_TYPES.join(","),
                    multiple: multipleFileUploadsAllowed,
                  }}
                  onChange={handleOnFileUpload}
                />

                <Button
                  disabled={isAddingToCart || maxFilesReached}
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<AddIcon />}
                  component="span"
                >
                  {props.translations.uploadArtworkButtonText}
                </Button>
              </label>
            </Box>
            <Box>
              <Typography variant="h6">You can:</Typography>
              <List dense>
                {props?.translations.uploadArtworkFeatures?.map(
                  (feature, idx) => {
                    return (
                      <GreenTicksListItem key={idx} variant="body2">
                        {feature.replace("{{maxDesigns}}", maxFiles.toString())}
                      </GreenTicksListItem>
                    );
                  }
                )}
              </List>
            </Box>
            <Box>
              {files.map((fs) => {
                return (
                  <Grid key={fs.id} container sx={{ height: "50px" }}>
                    <Grid
                      item
                      xs={5}
                      sx={{
                        lineHeight: "50px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        paddingRight: 1,
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      <i title={fs.id.toString()}>{fs.f.name}</i>
                    </Grid>
                    <Grid item xs={5}>
                      <LinearProgress
                        variant="determinate"
                        value={fs.uploadProgress * 100}
                        sx={{ marginTop: "21px" }}
                        color={
                          fs.uploadState === "uploaded" ? "success" : "primary"
                        }
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                      <IconButton onClick={() => handleRemoveFile(fs.id)}>
                        <TrashIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
            <TextField
              id="outlined-multiline-static"
              variant="outlined"
              label="Notes / Instructions"
              multiline
              rows={4}
              placeholder="Add proofing instructions"
              fullWidth
              sx={{ fontSize: "12px", marginTop: 2 }}
              value={instructions}
              onChange={(ev) => setInstructions(ev.target.value)}
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Grid
              width="100%"
              container
              spacing={{ xs: 0, md: 2 }}
              flexDirection={{ xs: "column-reverse", md: "row" }}
              sx={{ paddingTop: { xs: 2, md: 0 } }}
            >
              <Grid item xs={12} md={5}>
                <Button
                  disabled={isUploading || isAddingToCart}
                  type="submit"
                  variant="text"
                  color="primary"
                  sx={{
                    marginTop: { xs: 1, md: 2 },
                    marginBottom: { xs: 0, md: 2 },
                  }}
                  fullWidth
                  onClick={() => {
                    setFiles([]);
                  }}
                >
                  {/* {isSubmitting ? "Loading..." : t("submitButtonText")} */}
                  {/* {isLoading ? "Loading..." : "Retrieve order"} */}
                  {props?.translations.uploadArtworkSkipText}
                </Button>
              </Grid>
              <Grid item xs={12} md={7}>
                <Button
                  disabled={isUploading || isAddingToCart}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    marginTop: { xs: 1, md: 2 },
                    marginBottom: { xs: 0, md: 2 },
                  }}
                  fullWidth
                >
                  {/* {isSubmitting ? "Loading..." : t("submitButtonText")} */}
                  {/* {isLoading ? "Loading..." : "Retrieve order"} */}
                  Continue
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default StartDesign;
